import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  profitIntelligenceApi,
  type ContributionMarginSnapshot,
  type FinancialExperimentLink,
  type FinancialRecommendation,
  type FinancialScenario,
  type FinancialVersion,
  type LeakageItem,
  type ProfitForecast,
  type ProfitOpportunity,
  type ProfitTimelineEvent,
  type RecoveryClaim,
  type ReconciliationResult,
  type SettlementRecord,
  type TrueProfitSnapshot,
} from '../../api/profit-intelligence-dev109-runtime';

type Theme = 'light' | 'dark';
type Tab = 'profit' | 'leakage' | 'settlements' | 'recovery' | 'forecast' | 'scenarios' | 'recommendations' | 'opportunities' | 'versions' | 'experiments' | 'timeline';

const DEFAULT_ENTITY = 'B0CX123456';
const priorityCls = (p: string) =>
  p === 'critical' || p === 'high' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
    : p === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';

export function ProfitIntelligencePage() {
  const { entityId: routeEntity } = useParams<{ entityId: string }>();
  const entityId = routeEntity ?? DEFAULT_ENTITY;
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('profit');
  const [profit, setProfit] = useState<TrueProfitSnapshot | null>(null);
  const [contribution, setContribution] = useState<ContributionMarginSnapshot | null>(null);
  const [leakages, setLeakages] = useState<LeakageItem[]>([]);
  const [settlements, setSettlements] = useState<SettlementRecord[]>([]);
  const [reconciliation, setReconciliation] = useState<ReconciliationResult[]>([]);
  const [recovery, setRecovery] = useState<RecoveryClaim[]>([]);
  const [forecast, setForecast] = useState<ProfitForecast | null>(null);
  const [scenarios, setScenarios] = useState<FinancialScenario[]>([]);
  const [recommendations, setRecommendations] = useState<FinancialRecommendation[]>([]);
  const [opportunities, setOpportunities] = useState<ProfitOpportunity[]>([]);
  const [versions, setVersions] = useState<FinancialVersion[]>([]);
  const [experiments, setExperiments] = useState<FinancialExperimentLink[]>([]);
  const [timeline, setTimeline] = useState<ProfitTimelineEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = dark ? 'border-amber-900/40 bg-zinc-900' : 'border-amber-200 bg-white';
  const muted = dark ? 'text-zinc-400' : 'text-slate-500';
  const shell = dark ? 'bg-zinc-950 text-zinc-100' : 'bg-amber-50/20 text-slate-900';
  const accent = dark ? 'text-amber-300' : 'text-amber-700';
  const tabActive = 'bg-amber-600 text-white';

  const refresh = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const [p, cm, leak, sett, recon, rec, fc, scn, recs, opps, vers, exps, tl] = await Promise.all([
        profitIntelligenceApi.profit(entityId), profitIntelligenceApi.contribution(entityId),
        profitIntelligenceApi.leakage(entityId), profitIntelligenceApi.settlements(entityId),
        profitIntelligenceApi.reconciliation(entityId), profitIntelligenceApi.recovery(entityId),
        profitIntelligenceApi.forecast(entityId), profitIntelligenceApi.scenarios(entityId),
        profitIntelligenceApi.recommendations(entityId), profitIntelligenceApi.opportunities(entityId),
        profitIntelligenceApi.versions(entityId), profitIntelligenceApi.experiments(entityId),
        profitIntelligenceApi.history(entityId),
      ]);
      setProfit(p); setContribution(cm); setLeakages(leak); setSettlements(sett);
      setReconciliation(recon); setRecovery(rec); setForecast(fc); setScenarios(scn);
      setRecommendations(recs); setOpportunities(opps); setVersions(vers); setExperiments(exps); setTimeline(tl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load profit intelligence');
    } finally { setLoading(false); }
  }, [entityId]);

  useEffect(() => { refresh(); }, [refresh]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'profit', label: 'True Profit' }, { id: 'leakage', label: 'Leakage' },
    { id: 'settlements', label: 'Settlements' }, { id: 'recovery', label: 'Recovery' },
    { id: 'forecast', label: 'Forecast' }, { id: 'scenarios', label: 'Scenarios' },
    { id: 'recommendations', label: 'Recommendations' }, { id: 'opportunities', label: 'Opportunities' },
    { id: 'versions', label: 'Versions' }, { id: 'experiments', label: 'Experiments' }, { id: 'timeline', label: 'Timeline' },
  ];

  const totalRecoverable = leakages.reduce((sum, l) => {
    const m = l.estimated_recovery?.match(/\$[\d,]+/);
    return sum + (m ? parseInt(m[0].replace(/[$,]/g, ''), 10) : 0);
  }, 0);

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main">
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>DEV-000109 · Profit Intelligence · Observe → Recover → Learn</p>
            <h1 className="text-xl font-semibold">{profit?.entity_name ?? 'Product'} — {entityId}</h1>
            {profit && (
              <div className={`mt-2 flex flex-wrap gap-x-4 text-sm ${muted}`}>
                <span>Net Profit <strong className={accent}>{profit.net_profit}</strong></span>
                <span>Margin <strong className={accent}>{profit.margin_percent}</strong></span>
                <span>{profit.dna_origin ? 'DNA-backed' : 'Pending DNA'}</span>
                {leakages.length > 0 && <span>{leakages.length} leakages · ~${totalRecoverable.toLocaleString()} recoverable</span>}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to={`/products/${entityId}/workspace`} className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>ASIN 360</Link>
            <Link to="/platform/profit-intelligence-dev109" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Sprint Center</Link>
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>{dark ? 'Light' : 'Dark'}</button>
          </div>
        </div>
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'profit' && (
        <div className="space-y-4">
          {contribution && (
            <div className={`rounded-xl border p-4 ${card}`}>
              <p className="text-sm font-semibold">Contribution Margin</p>
              <div className={`mt-2 flex flex-wrap gap-4 text-sm ${muted}`}>
                <span>CM1 {contribution.cm1}</span><span>CM2 {contribution.cm2}</span><span>CM3 {contribution.cm3}</span>
                <span>Margin {contribution.margin_pct}</span><span>{contribution.trend}</span>
              </div>
            </div>
          )}
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {loading ? <div className="h-20 animate-pulse rounded-xl bg-slate-200" /> : profit?.components.map((c) => (
              <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
                <div className="flex items-center justify-between">
                  <p className="font-semibold">{c.name}</p>
                  {c.priority && <span className={`rounded px-2 py-0.5 text-xs ${priorityCls(c.priority)}`}>{c.priority}</span>}
                </div>
                <p className={`mt-1 text-2xl font-bold ${accent}`}>{c.current_value}</p>
                {c.gap && <p className={`text-xs ${muted}`}>{c.gap}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'leakage' && leakages.map((l) => (
        <div key={l.id} className={`rounded-xl border p-4 ${card}`}>
          <span className={`rounded px-2 py-0.5 text-xs ${priorityCls(l.priority)}`}>{l.priority}</span>
          <h3 className="mt-1 font-semibold">{l.title}</h3>
          <p className={`text-sm ${accent}`}>{l.estimated_recovery} recoverable · {(l.confidence * 100).toFixed(0)}% confidence</p>
          <p className={`text-xs ${muted}`}>{l.root_cause}</p>
          {l.suggested_action && <p className={`mt-1 text-sm ${accent}`}>→ {l.suggested_action}</p>}
        </div>
      ))}

      {tab === 'settlements' && (
        <div className="space-y-3">
          {settlements.map((s) => (
            <div key={s.id} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{s.id} — {s.period}</p>
              <p className={`text-sm ${muted}`}>{s.total_amount} · {s.status} {s.variance && `· Variance ${s.variance}`}</p>
            </div>
          ))}
          {reconciliation.map((r) => (
            <div key={r.type} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium capitalize">{r.type} reconciliation — {r.status}</p>
              <p className={`text-xs ${muted}`}>Matched {r.matched} · Unmatched {r.unmatched} {r.variance && `· ${r.variance}`}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'recovery' && recovery.map((c) => (
        <div key={c.id} className={`flex justify-between rounded-xl border p-4 ${card}`}>
          <div>
            <p className="font-medium">{c.title}</p>
            <p className={`text-xs ${muted}`}>{c.type} · {c.status}</p>
          </div>
          <div className="text-right">
            <p className={`font-semibold ${accent}`}>{c.expected_recovery}</p>
            {c.recovered_amount && <p className={`text-xs text-emerald-600`}>Recovered {c.recovered_amount}</p>}
          </div>
        </div>
      ))}

      {tab === 'forecast' && forecast && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p className="text-2xl font-bold">{forecast.net_profit} <span className={`text-sm font-normal ${muted}`}>projected net profit</span></p>
          <p className={`text-sm ${muted}`}>{forecast.expected_revenue} revenue · {forecast.gross_profit} gross · {(forecast.confidence * 100).toFixed(0)}% confidence</p>
          <p className={`mt-2 text-sm ${accent}`}>Range: {forecast.alternative_low} – {forecast.alternative_high}</p>
          {forecast.forecast_drivers && <ul className={`mt-2 list-disc pl-5 text-xs ${muted}`}>{forecast.forecast_drivers.map((d) => <li key={d}>{d}</li>)}</ul>}
        </div>
      )}

      {tab === 'scenarios' && (
        <div className="grid gap-3 sm:grid-cols-3">
          {scenarios.map((s) => (
            <div key={s.id} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{s.name}</p>
              <p className={`text-lg font-bold ${accent}`}>{s.profit}</p>
              <p className={`text-xs ${muted}`}>{s.cash_flow} · Recovery {s.recovery}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && recommendations.map((r) => (
        <div key={r.id} className={`rounded-xl border p-4 ${card}`}>
          <span className={`rounded px-2 py-0.5 text-xs ${priorityCls(r.priority)}`}>{r.priority}</span>
          <h3 className="mt-1 font-semibold">{r.title}</h3>
          <p className={`text-sm ${muted}`}>{r.business_objective}</p>
          {r.dna_evidence && <span className={`text-xs ${accent}`}>✓ DNA evidence</span>}
          <p className={`mt-1 text-xs ${muted}`}>{r.expected_recovery ?? r.expected_profit} · {(r.confidence * 100).toFixed(0)}% confidence</p>
        </div>
      ))}

      {tab === 'opportunities' && opportunities.map((o) => (
        <div key={o.id} className={`rounded-xl border p-3 ${card}`}>
          <p className="font-medium">{o.title}</p>
          <p className={`text-xs ${muted}`}>{o.type} · {o.expected_recovery ?? o.expected_profit} · <span className={priorityCls(o.priority)}>{o.priority}</span></p>
        </div>
      ))}

      {tab === 'versions' && versions.map((v) => (
        <div key={v.id} className={`flex justify-between rounded-xl border p-3 ${card}`}>
          <div><p className="font-medium">v{v.version} {v.type} — {v.status}</p><p className={`text-xs ${muted}`}>{v.summary}</p></div>
          <span>{v.approved ? 'Approved' : 'Draft'}</span>
        </div>
      ))}

      {tab === 'experiments' && experiments.map((e) => (
        <div key={e.id} className={`rounded-xl border p-3 ${card}`}>
          <p className="font-medium">{e.name}</p>
          <p className={`text-xs ${muted}`}>{e.status} · {e.baseline_kpi}</p>
        </div>
      ))}

      {tab === 'timeline' && timeline.map((e) => (
        <div key={e.id} className={`flex gap-3 border-b py-2 ${card}`}>
          <time className={`shrink-0 text-xs ${muted}`}>{new Date(e.timestamp).toLocaleString()}</time>
          <span className="text-sm">{e.title}</span>
        </div>
      ))}
    </div>
  );
}
